/**
 * One `get-subscription-status` request per user at a time.
 *
 * Two providers ask for the same server-evaluated status: UnifiedAuthProvider
 * (auth-level tier) and SubscriptionProvider (UI state). On 03-10 production
 * they fired together, several times per auth event, in bursts of 3–5 identical
 * GETs within ~1.5s. Concurrent callers for the same user now share the one
 * in-flight request; each gets its own clone of the response so either may read
 * the body. Nothing is cached once the request settles — a later call always
 * goes to the server, so real changes (checkout return, portal return, sign-in)
 * are never masked.
 */
import { secureFetch } from '@/lib/api-client';
import type { GetSubscriptionStatusResponse } from '@/types/dto';

const inFlight = new Map<string, Promise<Response>>();

/** Raw response for `userId`'s subscription status; concurrent calls share one request. */
export function requestSubscriptionStatus(userId: string): Promise<Response> {
  let pending = inFlight.get(userId);
  if (!pending) {
    pending = secureFetch('get-subscription-status', { method: 'GET' });
    inFlight.set(userId, pending);
    const clear = () => {
      if (inFlight.get(userId) === pending) inFlight.delete(userId);
    };
    pending.then(clear, clear);
  }
  return pending.then((response) => response.clone());
}

/** Parsed and schema-validated status. Throws on non-OK, bad JSON or bad shape. */
export async function fetchSubscriptionStatus(userId: string): Promise<GetSubscriptionStatusResponse> {
  const response = await requestSubscriptionStatus(userId);
  if (!response.ok) {
    throw Object.assign(new Error(`get-subscription-status failed with status ${response.status}`), {
      status: response.status,
    });
  }
  // Loaded lazily, as the callers did before (keeps dto out of the entry chunk).
  const { getSubscriptionStatusResponseSchema } = await import('@/types/dto');
  const parsed = getSubscriptionStatusResponseSchema.safeParse(await response.json());
  if (!parsed.success) throw new Error('Invalid server response');
  return parsed.data;
}

/** Test seam: forget any in-flight request. */
export function __resetSubscriptionStatusRequestsForTests(): void {
  inFlight.clear();
}
